import { request } from "$/lib/api"
import storeBikeQueries from "$/lib/queries/store-bike-queries"
import {
  CreateTireStorage,
  DiscardTireStorage,
  Route,
  SaveTireStorageSettings,
  SendTireStorageReminder,
  SentTireStorageReminder,
  TireSeason,
  TireStorageDefaults,
  TireStorageExpiringCount,
  TireStorageListFilter,
  TireStorageListItem,
  TireStorageReminderPreview,
  TireStorageSettings,
  UpdateTireStorage,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

export interface TireStorageListParams {
  filter: TireStorageListFilter
  season?: TireSeason
  search?: string
}

const fetchList = (
  storeId: string,
  { search, season, filter }: TireStorageListParams,
): Promise<TireStorageListItem[]> =>
  request<TireStorageListItem[]>({
    url: populateRoute(Route.TIRE_STORAGE__FIND_MANY, storeId),
    method: "GET",
    params: {
      filter,
      ...(season ? { season } : {}),
      ...(search ? { search } : {}),
    },
  })

const fetchSearch = (
  storeId: string,
  query: string,
): Promise<TireStorageListItem[]> =>
  request<TireStorageListItem[]>({
    url: populateRoute(Route.TIRE_STORAGE__SEARCH, storeId),
    method: "GET",
    params: { query },
  })

const fetchDetail = (
  storeId: string,
  tireStorageId: string,
): Promise<TireStorageListItem> =>
  request<TireStorageListItem>({
    url: populateRoute(Route.TIRE_STORAGE__FIND_ONE, storeId, tireStorageId),
    method: "GET",
  })

const fetchExpiringCount = (
  storeId: string,
): Promise<TireStorageExpiringCount> =>
  request<TireStorageExpiringCount>({
    url: populateRoute(Route.TIRE_STORAGE__EXPIRING_COUNT, storeId),
    method: "GET",
  })

const fetchDefaults = (
  storeId: string,
  storeCustomerId: string,
): Promise<TireStorageDefaults> =>
  request<TireStorageDefaults>({
    url: populateRoute(Route.TIRE_STORAGE__DEFAULTS, storeId),
    method: "GET",
    params: { storeCustomerId },
  })

const fetchSettings = (storeId: string): Promise<TireStorageSettings> =>
  request<TireStorageSettings>({
    url: populateRoute(Route.TIRE_STORAGE__GET_SETTINGS, storeId),
    method: "GET",
  })

const fetchReminderPreview = (
  storeId: string,
  season: TireSeason | undefined,
): Promise<TireStorageReminderPreview> =>
  request<TireStorageReminderPreview>({
    url: populateRoute(Route.TIRE_STORAGE__REMINDER_PREVIEW, storeId),
    method: "GET",
    params: season ? { season } : {},
  })

const tireStorageQueries = {
  all: () => ["tire-storages"],
  list: (storeId: string, params: TireStorageListParams) =>
    queryOptions({
      queryKey: [...tireStorageQueries.all(), "list", storeId, params],
      queryFn: () => fetchList(storeId, params),
    }),
  search: (storeId: string, query: string) =>
    queryOptions({
      queryKey: [...tireStorageQueries.all(), "search", storeId, query],
      queryFn: () => fetchSearch(storeId, query),
    }),
  detail: (storeId: string, tireStorageId: string) =>
    queryOptions({
      queryKey: [...tireStorageQueries.all(), "detail", storeId, tireStorageId],
      queryFn: () => fetchDetail(storeId, tireStorageId),
    }),
  expiringCount: (storeId: string) =>
    queryOptions({
      queryKey: [...tireStorageQueries.all(), "expiring-count", storeId],
      queryFn: () => fetchExpiringCount(storeId),
    }),
  // Never cached: the price depends on the customer's membership right now.
  defaults: (storeId: string, storeCustomerId: string) =>
    queryOptions({
      queryKey: [
        ...tireStorageQueries.all(),
        "defaults",
        storeId,
        storeCustomerId,
      ],
      queryFn: () => fetchDefaults(storeId, storeCustomerId),
      gcTime: 0,
    }),
  settings: (storeId: string) =>
    queryOptions({
      queryKey: [...tireStorageQueries.all(), "settings", storeId],
      queryFn: () => fetchSettings(storeId),
    }),
  // Never cached: the dialog reads the text once when it opens.
  reminderPreview: (storeId: string, season: TireSeason | undefined) =>
    queryOptions({
      queryKey: [
        ...tireStorageQueries.all(),
        "reminder-preview",
        storeId,
        season ?? "all",
      ],
      queryFn: () => fetchReminderPreview(storeId, season),
      gcTime: 0,
    }),
}

export const useTireStorageMutations = (storeId: string) => {
  const queryClient = useQueryClient()

  // A storage shows on its bike and its customer's bikes too.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: tireStorageQueries.all() }),
      queryClient.invalidateQueries({ queryKey: storeBikeQueries.all() }),
    ])

  const createMutation = useMutation({
    mutationFn: (data: CreateTireStorage) =>
      request<TireStorageListItem>({
        url: populateRoute(Route.TIRE_STORAGE__CREATE, storeId),
        method: "POST",
        data,
      }),
    onSettled: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      tireStorageId,
      data,
    }: {
      tireStorageId: string
      data: UpdateTireStorage
    }) =>
      request<TireStorageListItem>({
        url: populateRoute(Route.TIRE_STORAGE__UPDATE, storeId, tireStorageId),
        method: "PATCH",
        data,
      }),
    onSettled: invalidate,
  })

  const markReturnedMutation = useMutation({
    mutationFn: (tireStorageId: string) =>
      request<TireStorageListItem>({
        url: populateRoute(
          Route.TIRE_STORAGE__MARK_RETURNED,
          storeId,
          tireStorageId,
        ),
        method: "POST",
      }),
    onSettled: invalidate,
  })

  const discardMutation = useMutation({
    mutationFn: ({
      tireStorageId,
      data,
    }: {
      tireStorageId: string
      data: DiscardTireStorage
    }) =>
      request<TireStorageListItem>({
        url: populateRoute(Route.TIRE_STORAGE__DISCARD, storeId, tireStorageId),
        method: "POST",
        data,
      }),
    onSettled: invalidate,
  })

  const saveSettingsMutation = useMutation({
    mutationFn: (data: SaveTireStorageSettings) =>
      request<TireStorageSettings>({
        url: populateRoute(Route.TIRE_STORAGE__UPDATE_SETTINGS, storeId),
        method: "PUT",
        data,
      }),
    onSettled: invalidate,
  })

  const sendReminderMutation = useMutation({
    mutationFn: (data: SendTireStorageReminder) =>
      request<SentTireStorageReminder>({
        url: populateRoute(Route.TIRE_STORAGE__SEND_REMINDER, storeId),
        method: "POST",
        data,
      }),
  })

  return {
    createMutation,
    updateMutation,
    markReturnedMutation,
    discardMutation,
    saveSettingsMutation,
    sendReminderMutation,
  }
}

export default tireStorageQueries
